import { useCallback, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const EXPAND_ANIM_MS = 200;

export type ExpandableCardProps = {
    children: React.ReactNode;
    className?: string;
    /** Shared id for future View Transitions upgrade. */
    expandId?: string;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchEnd?: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchCancel?: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void;
};

/**
 * List/card row with a brief scale press before selection (shared-element-ish feel).
 * Calls onClick after the press micro-interaction starts so PagePush can open in parallel.
 */
const ExpandableCard: React.FC<ExpandableCardProps> = ({
    children,
    className = "",
    expandId,
    onClick,
    onContextMenu,
    onTouchStart,
    onTouchEnd,
    onTouchCancel,
    onTouchMove,
}) => {
    const [expanding, setExpanding] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const triggerExpand = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!onClick) return;
            setExpanding(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setExpanding(false);
                timerRef.current = null;
            }, EXPAND_ANIM_MS);
            onClick(e);
        },
        [onClick]
    );

    return (
        <div
            className={twMerge("ios-expand-row", className)}
            data-expand-id={expandId}
            data-expanding={expanding ? "true" : "false"}
            onClick={onClick ? triggerExpand : undefined}
            onContextMenu={onContextMenu}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            onTouchCancel={onTouchCancel}
            onTouchMove={onTouchMove}
        >
            {children}
        </div>
    );
};

export default ExpandableCard;
